import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Clock, Send, Smartphone, Users } from "lucide-react";
import {
  audienceCount,
  fetchCampaigns,
  fetchVariantStats,
  saveCampaign,
  sendCampaign,
} from "@/lib/queries";
import { dateTime, number, titleCase } from "@/lib/format";
import type { Campaign } from "@/lib/types";

type SegmentKey = "all" | "free" | "pro" | "inactive_7d" | "streak_risk" | "new_7d";

const segments: Array<{ key: SegmentKey; label: string; description: string; json: Record<string, unknown> }> = [
  { key: "all", label: "Everyone", description: "All active installs", json: { type: "all" } },
  { key: "free", label: "Free plan", description: "Free users only", json: { plan: "free" } },
  { key: "pro", label: "Pro", description: "Paying and trial users", json: { plan: "pro" } },
  { key: "inactive_7d", label: "Lapsed 7d", description: "No study for a week", json: { inactive_days: 7 } },
  { key: "streak_risk", label: "Streak at risk", description: "Studied yesterday, not today", json: { streak_risk: true } },
  { key: "new_7d", label: "New this week", description: "Joined in the last 7 days", json: { new_days: 7 } },
];

function segmentLabel(segment: Record<string, unknown>): string {
  const found = segments.find((item) => JSON.stringify(item.json) === JSON.stringify(segment));
  return found?.label ?? titleCase(Object.keys(segment)[0] ?? "custom");
}

function statusBadge(status: Campaign["status"]) {
  const map = {
    draft: "",
    scheduled: "badge-sunshine",
    sending: "badge-sky",
    sent: "badge-mint",
    cancelled: "badge-danger",
  } as const;
  return `badge ${map[status]}`;
}

export function PushPage() {
  const queryClient = useQueryClient();
  const [segment, setSegment] = useState<SegmentKey>("all");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [titleB, setTitleB] = useState("");
  const [bodyB, setBodyB] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const segmentJson = useMemo(
    () => segments.find((item) => item.key === segment)?.json ?? { type: "all" },
    [segment],
  );

  const campaigns = useQuery({ queryKey: ["campaigns"], queryFn: fetchCampaigns });
  const audience = useQuery({
    queryKey: ["audience", segment],
    queryFn: () => audienceCount(segmentJson),
  });
  const variantStats = useQuery({
    queryKey: ["push-variant-stats"],
    queryFn: () => fetchVariantStats(),
  });

  // Notification log rows only exist for messages FCM accepted, so `sent` is
  // delivered sends and `opened` is first-tap opens. Aggregate every campaign
  // that used variant copy; single-variant sends all land in "a".
  const ab = useMemo(() => {
    const totals = { a: { sent: 0, opened: 0 }, b: { sent: 0, opened: 0 } };
    const campaignsWithB = new Set<string>();
    for (const row of variantStats.data ?? []) {
      const key = row.variant === "b" ? "b" : "a";
      totals[key].sent += row.sent;
      totals[key].opened += row.opened;
      if (row.variant === "b") campaignsWithB.add(row.campaign_id);
    }
    return { a: totals.a, b: totals.b, campaigns: campaignsWithB.size };
  }, [variantStats.data]);

  const hasAbSends = ab.b.sent > 0;
  const sampleIsThin = ab.a.sent < 100 || ab.b.sent < 100;
  const winner =
    hasAbSends && !sampleIsThin && ab.a.sent > 0 && ab.b.sent > 0
      ? ab.b.opened / ab.b.sent > ab.a.opened / ab.a.sent
        ? "b"
        : ab.b.opened / ab.b.sent < ab.a.opened / ab.a.sent
          ? "a"
          : "tie"
      : null;

  const openRate = (sent: number, opened: number) =>
    sent > 0 ? `${Math.round((opened / sent) * 100)}%` : "—";

  const save = useMutation({
    mutationFn: (status: Campaign["status"]) =>
      saveCampaign({
        name: name.trim() || "Untitled campaign",
        segment: segmentJson,
        title_a: title.trim(),
        body_a: body.trim(),
        title_b: titleB.trim() || null,
        body_b: bodyB.trim() || null,
        deep_link: deepLink.trim() || null,
        scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : null,
        status,
      }),
    onSuccess: (campaign) => {
      setNotice(`Saved “${campaign.name}” as ${campaign.status}.`);
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (error: Error) => setNotice(error.message),
  });

  const send = useMutation({
    mutationFn: sendCampaign,
    onSuccess: (result) => {
      setNotice(`Sent to ${number(result.sent)} devices (${number(result.failed)} failed).`);
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      void queryClient.invalidateQueries({ queryKey: ["push-variant-stats"] });
    },
    onError: (error: Error) => setNotice(error.message),
  });

  const canSave = title.trim().length > 0 && body.trim().length > 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>Notifications that respect the learner</h2>
          <p>Max 2/day by default, quiet hours 22:00–08:00, every category opt-out.</p>
        </div>
        <span className="badge badge-grape">
          <Bell size={12} />
          FCM + local WorkManager
        </span>
      </div>

      {notice && <div className="banner banner-info">{notice}</div>}

      {!audience.isLoading && audience.data === 0 && (
        <div className="banner banner-info">
          No devices have registered for push yet. Add <code>google-services.json</code> to the
          Android app and set the <code>FCM_SERVICE_ACCOUNT</code> secret, then reinstall and open
          the app once.
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)" }}>
        <section className="card">
          <div className="card-head">
            <div>
              <h2>New campaign</h2>
              <p>Copy is sent to the push-send function when you press send.</p>
            </div>
          </div>
          <div className="card-pad stack">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="name">Internal name</label>
                <input
                  id="name"
                  className="input"
                  placeholder="Sunday recap · Oct 25"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="segment">Audience</label>
                <select
                  id="segment"
                  className="select"
                  value={segment}
                  onChange={(event) => setSegment(event.target.value as SegmentKey)}
                >
                  {segments.map((item) => (
                    <option key={item.key} value={item.key}>
                      {item.label} — {item.description}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="title">Title A</label>
                <input
                  id="title"
                  className="input"
                  maxLength={60}
                  placeholder="Your streak is waiting"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="titleB">Title B (optional)</label>
                <input
                  id="titleB"
                  className="input"
                  maxLength={60}
                  placeholder="A/B variant"
                  value={titleB}
                  onChange={(event) => setTitleB(event.target.value)}
                />
              </div>
              <div className="field span-2">
                <label htmlFor="body">Body A</label>
                <textarea
                  id="body"
                  className="textarea"
                  maxLength={180}
                  placeholder="Two minutes now keeps the streak alive."
                  value={body}
                  onChange={(event) => setBody(event.target.value)}
                />
                <span className="tiny faint">{body.length}/180</span>
              </div>
              <div className="field span-2">
                <label htmlFor="bodyB">Body B (optional)</label>
                <textarea
                  id="bodyB"
                  className="textarea"
                  maxLength={180}
                  placeholder="Leave empty for a single-variant send."
                  value={bodyB}
                  onChange={(event) => setBodyB(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="deepLink">Deep link</label>
                <input
                  id="deepLink"
                  className="input"
                  placeholder="streakly://study"
                  value={deepLink}
                  onChange={(event) => setDeepLink(event.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="schedule">Schedule (optional)</label>
                <input
                  id="schedule"
                  className="input"
                  type="datetime-local"
                  value={scheduledFor}
                  onChange={(event) => setScheduledFor(event.target.value)}
                />
              </div>
            </div>

            <div className="row" style={{ justifyContent: "flex-end" }}>
              <button
                className="btn btn-ghost"
                disabled={!canSave || save.isPending}
                onClick={() => save.mutate(scheduledFor ? "scheduled" : "draft")}
              >
                <Clock size={14} />
                {scheduledFor ? "Schedule" : "Save draft"}
              </button>
              <button
                className="btn btn-primary"
                disabled={!canSave || send.isPending || audience.data === 0}
                onClick={() => {
                  if (!window.confirm(`Send to ${number(audience.data ?? 0)} devices now?`)) return;
                  save.mutate("draft", {
                    onSuccess: (campaign) => send.mutate(campaign.id),
                  });
                }}
              >
                <Send size={14} />
                {send.isPending ? "Sending…" : "Save & send now"}
              </button>
            </div>
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <div>
              <h2>Audience</h2>
              <p>{segments.find((item) => item.key === segment)?.description}</p>
            </div>
            <div className="spacer" />
            <span className="badge badge-sky">
              <Users size={12} />
              {audience.isLoading ? "…" : number(audience.data ?? 0)}
            </span>
          </div>
          <div className="card-pad">
            <div
              style={{
                background: "var(--surface-2)",
                border: "1px solid var(--line)",
                borderRadius: 18,
                padding: 16,
              }}
            >
              <div className="row" style={{ alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: "var(--coral)",
                    display: "grid",
                    placeItems: "center",
                    color: "var(--ink)",
                    flex: "none",
                  }}
                >
                  <Smartphone size={17} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="tiny faint">FLASH QUIZ · now</div>
                  <div className="cell-strong" style={{ fontSize: 13 }}>
                    {title || "Notification title"}
                  </div>
                  <div className="small muted">
                    {body || "The body preview appears here as you type."}
                  </div>
                </div>
              </div>
            </div>

            <div className="divider" />
            <div className="stat-line">
              <span className="label">Category</span>
              <span className="value">Product update</span>
            </div>
            <div className="stat-line">
              <span className="label">Quiet hours</span>
              <span className="value">22:00–08:00</span>
            </div>
            <div className="stat-line">
              <span className="label">Daily cap</span>
              <span className="value">2 per learner</span>
            </div>
            <div className="stat-line">
              <span className="label">A/B split</span>
              <span className="value">{titleB && bodyB ? "50 / 50" : "single"}</span>
            </div>
            <p className="tiny faint" style={{ marginTop: 10 }}>
              Respects per-user opt-outs for product updates and offers. Transactional
              study reminders are separate.
            </p>
          </div>
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>A/B results</h2>
            <p>Delivered sends and opens per variant, all campaigns</p>
          </div>
          <div className="spacer" />
          <button
            className="btn btn-sm"
            onClick={() => void variantStats.refetch()}
            disabled={variantStats.isFetching}
          >
            Refresh
          </button>
        </div>

        <div className="card-pad stack" style={{ gap: 16 }}>
          {variantStats.isLoading && (
            <div className="empty"><div className="spinner" /></div>
          )}

          {!variantStats.isLoading && !hasAbSends && (
            <div className="empty">
              No A/B campaign has been sent yet. Fill in the variant B title and body
              when you create a campaign to split the audience 50/50.
            </div>
          )}

          {!variantStats.isLoading && hasAbSends && (
            <>
              {(["a", "b"] as const).map((key) => {
                const stats = ab[key];
                return (
                  <div
                    key={key}
                    className="row"
                    style={{ justifyContent: "space-between", alignItems: "center" }}
                  >
                    <div>
                      <h3 style={{ fontSize: 15, marginBottom: 4 }}>
                        Variant {key.toUpperCase()}
                      </h3>
                      <p className="tiny faint">
                        {key === "a" ? "Control copy" : "Test copy"}
                      </p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div className="stat-line">
                        <span className="label">Delivered</span>
                        <span className="value">{number(stats.sent)}</span>
                      </div>
                      <div className="stat-line">
                        <span className="label">Opened</span>
                        <span className="value">{number(stats.opened)}</span>
                      </div>
                      <div className="stat-line">
                        <span className="label">Open rate</span>
                        <span className="value">{openRate(stats.sent, stats.opened)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="divider" />

              <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h3 style={{ fontSize: 15, marginBottom: 4 }}>Leader</h3>
                  <p className="tiny faint">
                    Across {number(ab.campaigns)} campaign
                    {ab.campaigns === 1 ? "" : "s"} with variant copy
                  </p>
                </div>
                <div>
                  {winner === null && (
                    <span className="badge">Not enough data</span>
                  )}
                  {winner === "tie" && <span className="badge">Tied</span>}
                  {(winner === "a" || winner === "b") && (
                    <span className="badge badge-mint">
                      Variant {winner.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>

              <p className="tiny faint" style={{ margin: 0 }}>
                {sampleIsThin
                  ? "Fewer than 100 delivered sends per variant — treat this as a signal, not a result."
                  : "Descriptive comparison only. Nothing here is a significance test."}
                {" "}Opens are counted from the first notification tap per learner.
              </p>
            </>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Campaigns</h2>
            <p>Drafts, scheduled sends and results</p>
          </div>
        </div>
        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Segment</th>
                <th>Status</th>
                <th>Audience</th>
                <th>Sent</th>
                <th>Opened</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {campaigns.isLoading && (
                <tr><td colSpan={8} className="empty"><div className="spinner" /></td></tr>
              )}
              {!campaigns.isLoading && (campaigns.data ?? []).length === 0 && (
                <tr><td colSpan={8} className="empty">No campaigns yet.</td></tr>
              )}
              {(campaigns.data ?? []).map((campaign) => (
                <tr key={campaign.id}>
                  <td>
                    <div className="cell-strong">{campaign.name}</div>
                    <div className="cell-sub">{campaign.title_a}</div>
                  </td>
                  <td className="muted">{segmentLabel(campaign.segment)}</td>
                  <td><span className={statusBadge(campaign.status)}>{campaign.status}</span></td>
                  <td>{number(campaign.audience_size ?? 0)}</td>
                  <td>{number(campaign.sent_count)}</td>
                  <td>
                    {number(campaign.opened_count)}
                    {campaign.sent_count > 0 && (
                      <span className="faint small">
                        {" "}· {Math.round((campaign.opened_count / campaign.sent_count) * 100)}%
                      </span>
                    )}
                  </td>
                  <td className="nowrap faint">{dateTime(campaign.created_at)}</td>
                  <td>
                    {campaign.status !== "sent" && (
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={send.isPending}
                        onClick={() => {
                          if (!window.confirm(`Send “${campaign.name}” now?`)) return;
                          send.mutate(campaign.id);
                        }}
                      >
                        <Send size={11} /> Send
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
